import styled from 'styled-components';

type TextType = { color: string };
type BgBorderType = { background: string; border: string };
type BgType = { background: string };
type BadgeType = { background: string; color: string };

/* ── Page layout ── */

export const PageWrapper = styled.div<BgType>`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: ${props => props.background};
  box-sizing: border-box;
  overflow-y: auto;
`;

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 8px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Back header ── */

export const BackHeader = styled.button<TextType>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.color};
  font-size: 18px;
  font-weight: 700;
  padding: 0;

  &:hover {
    opacity: 0.75;
  }
`;

/* ── Extension header card ── */

export const ExtIconBadgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const ExtInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

export const ExtIconWrapper = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 14px;
  background: ${props => props.background};
`;

export const ExtBadge = styled.span<BadgeType>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  font-size: 11px;
  font-weight: 700;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 20px;
  padding: 4px 12px;
`;

export const RenewalNotice = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.background};
  border-radius: 8px;
  padding: 10px 14px;
`;

export const RenewalNoticeText = styled.span<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  line-height: 1.5;
  flex: 1;
`;

export const TrialNotice = styled.div<BadgeType>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.background};
  border-radius: 8px;
  padding: 10px 14px;
`;

export const TrialNoticeTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const TrialNoticeTitle = styled.span<TextType>`
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.color};
`;

export const TrialNoticeSubtitle = styled.span<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  line-height: 1.5;
`;

export const ExtName = styled.h2<TextType>`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0;
`;

export const ExtDescription = styled.p<TextType>`
  font-size: 14px;
  color: ${props => props.color};
  margin: 0;
  line-height: 1.6;
`;

/* ── Details grid ── */

export const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DetailLabel = styled.span<TextType>`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.color};
  letter-spacing: 0.5px;
`;

export const DetailValue = styled.span<TextType>`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.color};
`;

export const StatusBadge = styled.span<BadgeType>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: 20px;
  padding: 3px 10px;
  width: fit-content;
`;

/* ── Benefits 2-col ── */

export const BenefitsGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const BenefitText = styled.span<TextType>`
  font-size: 13px;
  color: ${props => props.color};
`;

/* ── Billing summary column ── */

export const BillingCard = styled.div<BgBorderType>`
  display: flex;
  flex-direction: column;
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 6px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  box-sizing: border-box;
`;

export const BillingCardSpacer = styled.div`
  flex: 1;
`;

export const BillingNote = styled.p<TextType>`
  font-size: 10px;
  font-style: italic;
  color: ${props => props.color};
  margin: 0;
`;

export const MainValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
  white-space: nowrap;
`;

export const MainValue = styled.span<TextType>`
  font-size: 38px;
  font-weight: 800;
  color: ${props => props.color};
  line-height: 1;
`;

export const MainValueSuffix = styled.span<TextType>`
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.color};
`;

export const ExplanatoryText = styled.p<TextType>`
  font-size: 13px;
  color: ${props => props.color};
  line-height: 1.5;
`;

export const Divider = styled.div<{ color: string }>`
  height: 1px;
  background: ${props => props.color};
  opacity: 0.3;
  margin: 16px 0;
`;

export const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SummaryExtensionName = styled.span<TextType>`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.color};
`;

export const SummaryLabel = styled.span<TextType>`
  font-size: 13px;
  color: ${props => props.color};
`;

export const SummaryLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SummaryValue = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.color};
`;

/* ── Next billing card ── */

export const NextBillingCard = styled.div<BgBorderType>`
  background: ${props => props.background};
  border: 1px solid ${props => props.border};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

export const NextBillingLabel = styled.p<TextType>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${props => props.color};
  margin: 0 0 4px 0;
`;

export const NextBillingDate = styled.p<TextType>`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color};
  margin: 0 0 4px 0;
`;

export const NextBillingNote = styled.p<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  margin: 0;
  line-height: 1.4;
`;

export const NextBillingNoteStrong = styled.span<TextType>`
    color: ${props => props.color};
  font-weight: 700;
`;

/* ── Footer ── */

export const FooterText = styled.p<TextType>`
  font-size: 12px;
  color: ${props => props.color};
  text-align: center;
  margin: 14px 0 0 0;
`;
